import type { CanonicalEntity } from '@/shared/api/types';

import { entityDisplayLabel, entityModuleOwnerId, entityObjectKind, isModuleEntity } from './entityDomain';

/*
 * ОСЬ ВЫДЕЛЕНИЯ ПРОХОДИТСЯ В ОБЕ СТОРОНЫ.
 *
 * docs/UI_TARGET_2026.md §7.5: ось «комната → композиция → модуль → деталь»
 * проходилась ТОЛЬКО ВНИЗ. Человек, провалившийся в деталь, возвращался наверх
 * через дерево слева — уход на другую поверхность ради шага, который у Figma
 * стоит одну клавишу. `Esc` снимал выделение целиком, а `⌘`-клик дублировал
 * `Shift`, то есть единственный свободный модификатор был потрачен впустую.
 *
 * Здесь заведён ОДИН источник этой оси — чистые функции без стора и без React.
 * Причина та же, по которой контекст выделения живёт в `selectionContext.ts`:
 * пока каждая поверхность считает родителя сама, они расходятся молча, и
 * «наверх» из сцены ведёт не туда, куда «наверх» из дерева.
 *
 * Ось НЕ выводится из `parentId` напрямую. В канонической сцене между модулем и
 * комнатой может не быть композиции вовсе, а у детали `parentId` иногда
 * указывает на служебный узел. Поэтому уровень определяется по роду объекта, а
 * родитель ищется ближайшим узлом СТРОГО ВЫШЕ по оси, а не первым попавшимся.
 */

export type AxisLevel = 'room' | 'composition' | 'module' | 'part';

/** Порядок оси сверху вниз. Индекс = глубина: у комнаты 0, у детали 3. */
export const AXIS_ORDER: readonly AxisLevel[] = ['room', 'composition', 'module', 'part'] as const;

export function axisDepth(level: AxisLevel): number {
  return AXIS_ORDER.indexOf(level);
}

function isRoomEntity(entity: CanonicalEntity | null | undefined): boolean {
  if (!entity) return false;
  const kind = String(entity.kind || '').toLowerCase();
  const objectKind = entityObjectKind(entity).toLowerCase();
  return kind === 'room' || objectKind === 'room' || objectKind === 'apartment' || objectKind === 'apartment_scene';
}

function isCompositionEntity(entity: CanonicalEntity | null | undefined): boolean {
  if (!entity) return false;
  return entityObjectKind(entity).toLowerCase() === 'composition';
}

/** На каком уровне оси находится объект. Всё, что не комната/композиция/модуль, — деталь. */
export function axisLevelOf(entity: CanonicalEntity | null | undefined): AxisLevel {
  if (isRoomEntity(entity)) return 'room';
  if (isCompositionEntity(entity)) return 'composition';
  if (isModuleEntity(entity)) return 'module';
  return 'part';
}

/** Подъём по `parentId` до первого узла, который проходит проверку. Цикл ловится `seen`. */
function ancestorMatching(
  entity: CanonicalEntity,
  byId: ReadonlyMap<string, CanonicalEntity>,
  matches: (candidate: CanonicalEntity) => boolean,
): CanonicalEntity | null {
  const seen = new Set<string>([entity.id]);
  let parentId = String(entity.parentId || '').trim();
  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = byId.get(parentId);
    if (!parent) return null;
    if (matches(parent)) return parent;
    parentId = String(parent.parentId || '').trim();
  }
  return null;
}

/**
 * Следующий узел ВВЕРХ по оси, или `null` на её верхнем конце.
 *
 * Деталь отдаёт модуль-хозяина (тем же `entityModuleOwnerId`, каким его считает
 * сцена, — вторая реализация разошлась бы), модуль отдаёт композицию либо
 * комнату, композиция — комнату, комната — ничего.
 */
export function axisParentId(
  id: string,
  byId: ReadonlyMap<string, CanonicalEntity>,
): string | null {
  const entity = byId.get(id);
  if (!entity) return null;
  const level = axisLevelOf(entity);

  if (level === 'room') return null;

  if (level === 'part') {
    const ownerId = entityModuleOwnerId(entity, byId as Map<string, CanonicalEntity>);
    if (ownerId && ownerId !== id && byId.has(ownerId)) return ownerId;
    // Хозяина-модуля нет: деталь лежит прямо в композиции или в комнате.
    const above = ancestorMatching(entity, byId, (c) => isCompositionEntity(c) || isRoomEntity(c));
    return above ? above.id : null;
  }

  if (level === 'module') {
    const above = ancestorMatching(entity, byId, (c) => isCompositionEntity(c) || isRoomEntity(c));
    return above ? above.id : null;
  }

  // composition
  const room = ancestorMatching(entity, byId, isRoomEntity);
  return room ? room.id : null;
}

export type AxisStepUp =
  | Readonly<{ kind: 'select'; ids: readonly string[]; level: AxisLevel; reason: string }>
  | Readonly<{ kind: 'clear'; reason: string }>
  | Readonly<{ kind: 'none'; reason: string }>;

/**
 * Шаг ВВЕРХ по оси для текущего выделения.
 *
 * Правило верхнего конца: снять выделение можно только с верхнего уровня. Пока
 * выше есть куда идти, `Esc` идёт туда — иначе клавиша выхода из детали
 * выбрасывает человека из работы целиком, что и происходило до правки.
 *
 * Отказ всегда назван словами: `reason` непуст в каждой ветке, включая ту, где
 * идти некуда.
 */
export function stepSelectionUp(
  selectedIds: readonly string[],
  byId: ReadonlyMap<string, CanonicalEntity>,
): AxisStepUp {
  const ids = selectedIds.filter((id) => typeof id === 'string' && id.length > 0);
  if (ids.length === 0) return { kind: 'none', reason: 'Ничего не выделено — подниматься не от чего' };

  const parents: string[] = [];
  const seen = new Set<string>();
  let anyMissing = false;
  for (const id of ids) {
    const parentId = axisParentId(id, byId);
    if (!parentId) { anyMissing = true; continue; }
    if (!seen.has(parentId)) { seen.add(parentId); parents.push(parentId); }
  }

  if (parents.length === 0) {
    return {
      kind: 'clear',
      reason: anyMissing
        ? 'Выделение уже на верхнем уровне оси — выделение снято'
        : 'Выше по оси ничего нет — выделение снято',
    };
  }

  const level = axisLevelOf(byId.get(parents[0]!));
  const label = parents.length === 1
    ? entityDisplayLabel(byId.get(parents[0]!), parents[0]!)
    : `объектов: ${parents.length}`;
  return {
    kind: 'select',
    ids: parents,
    level,
    reason: `Вверх по оси: ${label}`,
  };
}
